import {useAlgoliaShopConfig} from "@/providers/AlgoliaShopConfigProvider.tsx";
import type {AlgoliaAutocompleteHit} from "@/types/AlgoliaAutocompleteHit.ts";
import {type AutocompletePlugin} from "@algolia/autocomplete-core";
import type {SearchResponse} from "algoliasearch";
import Fuse from "fuse.js";


export const useCategoriesPlugin = () => {
    const {searchClient, algoliaConfig: {algoliaIndexName},} = useAlgoliaShopConfig();
    const categoriesPlugin: AutocompletePlugin<HierarchicalCategoriesItem, {}> = {
        getSources({query}) {
            if (!query) {
                return [];
            }
            return [
                {
                    sourceId: 'categoriesPlugin',
                    getItems() {
                        return searchClient
                            .search([
                                {
                                    indexName: algoliaIndexName,
                                    params: {
                                        hitsPerPage: 10,
                                        query,
                                        attributesToRetrieve: ["hierarchicalCategories"],
                                        typoTolerance: true,
                                    },
                                },
                            ])
                            .then((results: { results: SearchResponse<AlgoliaAutocompleteHit>[] }) => {
                                const hits = results.results[0].hits;
                                const uniqueCategoriesMap = new Map<string, HierarchicalCategoriesItem>();

                                hits.forEach((hit) => {
                                    const categories = hit.hierarchicalCategories;
                                    if (!categories) return;

                                    Object.keys(categories).forEach((level) => {
                                        const category = categories[level];
                                        if (category && category.name) {
                                            uniqueCategoriesMap.set(level + category.name + category.slug, category);
                                        }
                                    });
                                });

                                const uniqueCategories = [...uniqueCategoriesMap.values()];

                                // Use Fuse to rank categories by similarity to query
                                const fuse = new Fuse(uniqueCategories, {
                                    keys: ['name'],
                                    includeScore: true,
                                    threshold: 0.6, // adjust sensitivity (0.0 = exact match, 1.0 = match all)
                                });

                                const _results = fuse.search(query);

                                // Take top 10
                                return _results.slice(0, 10).map(result => result.item);
                            });
                    },
                },
            ];
        },
    };
    return categoriesPlugin;
}
